import { getWindow } from '@surfline/web-common';

export type BlogHerAds = NonNullable<Window['blogherads']>;

type BlogheradsSlot = {
  setDefaultAutoRefreshTime: (time: number) => BlogheradsSlot;
  addSize: (width: number, height: number) => BlogheradsSlot;
  setClsOptimization: (option: string) => BlogheradsSlot;
  setAutoRefreshTime: (time: number) => BlogheradsSlot;
  setTargeting: (target: string, value: string | number) => BlogheradsSlot;
  setSubAdUnitPath: (path: string) => { display: () => void };
};

/**
 * Creates a mock BlogheradsSlot object for fallback scenarios
 */
const createMockBlogheradsSlot = (): BlogheradsSlot => ({
  setDefaultAutoRefreshTime: () => createMockBlogheradsSlot(),
  addSize: () => createMockBlogheradsSlot(),
  setClsOptimization: () => createMockBlogheradsSlot(),
  setAutoRefreshTime: () => createMockBlogheradsSlot(),
  setTargeting: () => createMockBlogheradsSlot(),
  setSubAdUnitPath: () => ({ display: () => {} }),
});

/**
 * Creates a fallback BlogHerAds object with mock implementations
 */
const createFallbackBlogherads = (): BlogHerAds => ({
  adq: [],
  setTargeting: () => {},
  addEventListener: () => {},
  defineCustomSlot: (_currDivId: string, _customAdSize: number[]) => createMockBlogheradsSlot(),
  defineResponsiveSlot: (
    _adSizeMappingsSHE: Array<Array<number> | number | string>,
    _currDivId: string,
  ) => createMockBlogheradsSlot(),
  defineSlot: (_adSize: string, _currDivId: string) => createMockBlogheradsSlot(),
  destroySlots: (_slotsIdsToDestroy: Array<string>) => {},
  getVastTag: (_callback: (tag: string) => void) => {},
  reloadAds: () => {},
  removeEventListener: () => {},
  running: false,
  setConf: (_orientation: string, _name: string) => {},
  setDefaultAutoRefreshTime: () => {},
});

/**
 * Initializes the global blogherads object if it doesn't exist.
 * This utility centralizes the blogherads initialization logic that was
 * previously duplicated across multiple components.
 *
 * @returns The initialized blogherads object
 */
export const initializeBlogherads = (): BlogHerAds => {
  const win = getWindow();

  if (!win) {
    // Return a fallback object when window is not available (SSR, tests, etc.)
    return createFallbackBlogherads();
  }

  if (!win.blogherads) {
    win.blogherads = createFallbackBlogherads();
  }

  return win.blogherads as BlogHerAds;
};

/**
 * Gets the existing blogherads object or creates a fallback.
 * Use this when you need to access blogherads but don't want to
 * modify the global window object.
 *
 * @returns The blogherads object or a fallback with the same interface
 */
export const getBlogherads = (): BlogHerAds => {
  const win = getWindow();
  return (win?.blogherads as BlogHerAds) ?? createFallbackBlogherads();
};

/**
 * Safely adds a function to the blogherads ad queue.
 * This is a convenience method that handles the initialization
 * and queue management in one call.
 *
 * @param callback - Function to add to the ad queue
 */
export const addToBlogheradsQueue = (callback: () => void): void => {
  const blogherads = initializeBlogherads();
  blogherads.adq.push(callback);
};
